import { Fragment, useState, useEffect, useRef, useContext } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { useForm } from "react-hook-form"
import { XMarkIcon, TagIcon } from "@heroicons/react/24/outline"
import Cookies from "js-cookie"

import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import { subscribeToWaitingList } from "utils/forms"
import { Input, SubmitButton, PrivacyPolicyLink } from "components/atoms/form"
import Altcha from "components/atoms/Altcha"
import { COOKIE_NOTICE_ACCEPTED_NAME } from "utils/constants"

const COOKIE_NAME = "sb_waitlist_dismissed"
const COOKIE_EXPIRY_DAYS = 30
const POPUP_DELAY_MS = 500 // Small delay after cookie consent
const COOKIE_CHECK_INTERVAL_MS = 500 // How often to check for cookie consent

// Plausible event tracking
const trackEvent = (eventName: string) => {
  if (typeof window !== "undefined" && window.plausible) {
    window.plausible(eventName)
  }
}

type WaitingListPopupProps = {
  enabled?: boolean
}

const WaitingListPopup = ({ enabled = true }: WaitingListPopupProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartY = useRef(0)
  const altchaRef = useRef<{ value: string | null }>(null)
  const { locale } = useContext(LocaleContext)

  const {
    waitingListButton,
    waitingListSuccessTitle,
    waitingListSuccessBody,
    waitingListSuccessButton,
    formFieldEmail,
    formFieldName,
    formErrorGeneral,
    subscribePrivacyPolicy1,
  } = useTranslations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
    },
  })

  const [isSubmitError, setSubmitError] = useState(false)
  const [errMessage, setErrMessage] = useState(formErrorGeneral)

  useEffect(() => {
    if (!enabled) return

    // Check if user has already dismissed or submitted
    const dismissed = Cookies.get(COOKIE_NAME)
    if (dismissed) return

    let timer: NodeJS.Timeout | null = null
    let interval: NodeJS.Timeout | null = null

    const showPopupAfterDelay = () => {
      timer = setTimeout(() => {
        setIsOpen(true)
        trackEvent("WaitingList: Popup Shown")
      }, POPUP_DELAY_MS)
    }

    // Check if cookie consent has already been given
    const cookieConsentGiven = Cookies.get(COOKIE_NOTICE_ACCEPTED_NAME)
    if (cookieConsentGiven) {
      // Cookie consent already given, show popup after delay
      showPopupAfterDelay()
    } else {
      // Wait for cookie consent before showing popup
      interval = setInterval(() => {
        const consentNow = Cookies.get(COOKIE_NOTICE_ACCEPTED_NAME)
        if (consentNow) {
          // Cookie consent just given, clear interval and show popup
          if (interval) clearInterval(interval)
          showPopupAfterDelay()
        }
      }, COOKIE_CHECK_INTERVAL_MS)
    }

    return () => {
      if (timer) clearTimeout(timer)
      if (interval) clearInterval(interval)
    }
  }, [enabled])

  const closeModal = (markDismissed = true) => {
    if (markDismissed) {
      Cookies.set(COOKIE_NAME, "true", { expires: COOKIE_EXPIRY_DAYS })
      trackEvent("WaitingList: Popup Dismissed")
    }
    setIsOpen(false)
  }

  const onSubmit = async (data) => {
    try {
      const altcha = altchaRef.current?.value
      const res = await subscribeToWaitingList(data, locale, altcha)

      if (res.status === 200) {
        setShowSuccess(true)
        Cookies.set(COOKIE_NAME, "subscribed", { expires: 365 })
        trackEvent("WaitingList: Subscribed")
        reset()
        setSubmitError(false)
      }
    } catch (error: any) {
      const response = error?.response
      if (response?.data?.title === "Member Exists") {
        // Still show success if already subscribed
        setShowSuccess(true)
        Cookies.set(COOKIE_NAME, "subscribed", { expires: 365 })
      } else {
        setErrMessage(response?.data?.detail || formErrorGeneral)
        setSubmitError(true)
      }
    }
  }

  const handleSuccessClose = () => {
    setShowSuccess(false)
    closeModal(false)
  }

  // Touch handlers for mobile drawer swipe-to-close
  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY
    setIsDragging(true)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return
    const currentY = e.touches[0].clientY
    const diff = currentY - dragStartY.current
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setDragY(diff)
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    // Close if dragged more than 100px
    if (dragY > 100) {
      closeModal()
    }
    setDragY(0)
  }

  if (!enabled) return null

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => closeModal()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center text-center md:items-center md:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full md:translate-y-4 md:scale-95"
              enterTo="opacity-100 translate-y-0 md:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 md:scale-100"
              leaveTo="opacity-0 translate-y-full md:translate-y-4 md:scale-95"
            >
              <Dialog.Panel
                className={`relative w-full max-w-md transform overflow-hidden rounded-t-3xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6 pt-7 text-left shadow-2xl md:rounded-3xl md:p-8 ${
                  isDragging ? "" : "transition-transform duration-200"
                }`}
                style={{ transform: `translateY(${dragY}px)` }}
              >
                {/* Drawer handle - mobile only, touch target for swipe */}
                <div
                  className="absolute inset-x-0 top-0 flex h-8 cursor-grab items-center justify-center active:cursor-grabbing md:hidden"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="h-1 w-10 rounded-full bg-slate-300" />
                </div>

                {/* Close button */}
                <button
                  onClick={() => closeModal()}
                  className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                {!showSuccess ? (
                  <>
                    {/* Header with creative title treatment */}
                    <div className="mb-3">
                      {/* Launch date badge - floating announcement */}
                      <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white shadow-md shadow-emerald-200/50">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-white"></span>
                        </span>
                        Coming soon
                      </div>

                      {/* Main title with gradient */}
                      <Dialog.Title
                        as="h3"
                        className="mb-0 font-display text-2xl font-bold leading-tight"
                      >
                        <span className="bg-gradient-to-r from-slate-900 via-emerald-800 to-teal-700 bg-clip-text text-transparent">
                          AI Compass
                        </span>
                        <br />
                        <span className="text-lg font-semibold text-slate-500">
                          Foundations for{" "}
                        </span>
                        <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-lg font-semibold text-transparent">
                          Kids and Teens
                        </span>
                      </Dialog.Title>
                    </div>

                    <p className="mb-4 text-sm leading-relaxed text-slate-600">
                      A new lesson package designed to teach children essential
                      AI skills in a safe, engaging way. Sign up to get more
                      information about the teaching modules, get notified on
                      launch day, and unlock an exclusive early bird discount!
                    </p>

                    {/* Discount badge */}
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-2 text-sm font-medium text-amber-800 ring-1 ring-amber-200/50">
                      <TagIcon className="h-4 w-4" />
                      <span>10% Early bird discount included</span>
                    </div>

                    {/* Form */}
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="mb-0 space-y-4"
                    >
                      <Input
                        label={formFieldName}
                        name="name"
                        control={control}
                        type="text"
                        errors={errors}
                      />
                      <Input
                        label={formFieldEmail}
                        name="email"
                        control={control}
                        required
                        type="email"
                        errors={errors}
                      />

                      <div className="pt-2">
                        <Altcha
                          ref={altchaRef}
                          style={{
                            "--altcha-border-radius": "0.75rem",
                            "--altcha-color-border": "rgb(226, 232, 240)",
                            "--altcha-color-active": "#10b981",
                            "--altcha-color-border-focus": "#10b981",
                            "--altcha-color-base": "rgba(255, 255, 255, 0.8)",
                          }}
                        />
                      </div>

                      <div className="pt-1">
                        <PrivacyPolicyLink text={subscribePrivacyPolicy1} />
                      </div>

                      <div className="pt-2">
                        <SubmitButton
                          label={waitingListButton}
                          isSubmitting={isSubmitting}
                          isSubmitSuccessful={
                            isSubmitSuccessful && !isSubmitError
                          }
                          isSubmitError={isSubmitError}
                          errorMessage={errMessage}
                        />
                      </div>
                    </form>
                  </>
                ) : (
                  /* Success state */
                  <div className="py-4 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-lg shadow-emerald-200">
                      <svg
                        className="h-8 w-8 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <Dialog.Title
                      as="h3"
                      className="mb-2 font-display text-xl font-bold text-slate-900"
                    >
                      {waitingListSuccessTitle}
                    </Dialog.Title>
                    <p className="mb-6 text-sm text-slate-600">
                      {waitingListSuccessBody}
                    </p>
                    <button
                      type="button"
                      onClick={handleSuccessClose}
                      className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition-all hover:from-emerald-600 hover:to-teal-600 hover:shadow-xl"
                    >
                      {waitingListSuccessButton}
                    </button>
                  </div>
                )}

                {/* Decorative elements */}
                <div className="pointer-events-none absolute -bottom-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-emerald-200/40 to-teal-200/40 blur-3xl" />
                <div className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-teal-200/30 to-emerald-200/30 blur-2xl" />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default WaitingListPopup
